/**
 * Carts API Service (client-side)
 *
 * SOC 2 (C9): customer-facing by-GUID cart lookups previously ran a Firestore
 * `where('guid', ...)` LIST (and a collection-group LIST for the cross-app
 * handoff), which required a blanket `allow list` rule that let any
 * authenticated (incl. anonymous) session ENUMERATE every restaurant's carts
 * (customer UIDs + item-level contents). Those reads now go through the
 * authenticated API, which resolves a SINGLE record server-side via the admin
 * SDK (the unguessable GUID is the capability). The Firestore `carts` list
 * rule is now staff-only.
 */

import { Cart } from "@order-up/models";
import {
  createApiClient,
  deleteApi,
  getApi,
  postApi,
  putApi,
  type ApiEnvelope,
} from "./apiClient";

const apiClient = createApiClient("/api/v1/carts");
// Public/customer app (checkout): authenticate with the Firebase Bearer token
// (incl. the anonymous session), NOT a cross-domain session cookie — mirrors
// checkout.ts. Otherwise the browser attempts a credentialed cross-origin
// request that CORS rejects and that carries the wrong identity.
apiClient.defaults.withCredentials = false;

/**
 * The GUID resolves to a cart that already became an order. Terminal — the
 * caller must never render it as a live checkout. Carries the order it became
 * so a caller can send the customer to it instead.
 */
export class CartAlreadyConvertedError extends Error {
  readonly code = "CART_ALREADY_CONVERTED" as const;
  readonly orderGuid?: string;
  constructor(orderGuid?: string) {
    super("This cart has already been placed as an order.");
    this.name = "CartAlreadyConvertedError";
    this.orderGuid = orderGuid;
  }
}

/** Pull an HTTP status off whatever error shape the client layer surfaces. */
const statusOf = (error: unknown): number | undefined => {
  const e = error as { status?: number; response?: { status?: number } } | null;
  return e?.status ?? e?.response?.status;
};

const errorBodyOf = (error: unknown): Record<string, unknown> | undefined => {
  const e = error as { data?: unknown; response?: { data?: unknown } } | null;
  const body = (e?.response?.data ?? e?.data) as
    | { error?: Record<string, unknown> }
    | undefined;
  return body?.error;
};

/**
 * Resolve a single cart by its unguessable GUID. The GUID alone identifies it,
 * so no tenant or location id travels with the request (or in the checkout
 * URL). Returns the Cart model, or null if the GUID is unknown/expired.
 *
 * 404 → null. The route used to answer a missing cart with `200 + null`, but
 * its 200 schema is `type: "object"`, so Fastify serialized that to `{}` — a
 * truthy value that sailed past the null check and made `Cart.fromJSON` throw
 * "cartId must be a number" (prod 2026-07-30).
 *
 * 410 → CartAlreadyConvertedError. A converted cart is terminal, never a live
 * checkout.
 */
const getCartByGuid = async (cartGuid: string): Promise<Cart | null> => {
  let data: Record<string, unknown> | null;
  try {
    data = await getApi<Record<string, unknown> | null>(
      apiClient,
      `/by-guid/${encodeURIComponent(cartGuid)}`,
    );
  } catch (error) {
    const status = statusOf(error);
    if (status === 404) return null;
    if (status === 410) {
      const body = errorBodyOf(error);
      throw new CartAlreadyConvertedError(
        typeof body?.orderGuid === "string" ? body.orderGuid : undefined,
      );
    }
    throw error;
  }
  // Defensive: an empty object is not a cart. Keeps a future schema/serializer
  // regression from reaching Cart.fromJSON as a thrown parse error.
  if (!data || typeof data.cartId !== "number") return null;
  return Cart.fromJSON(data);
};

/**
 * Cart-handoff owner lookup (cross-app auth). Resolves ONLY the owner UID of a
 * cart by its unguessable GUID — no tenant/restaurant context, no cart
 * contents. Returns `{ found, customerId }`.
 */
const getCartOwnerByGuid = async (
  cartGuid: string,
): Promise<{ found: boolean; customerId: string | null }> => {
  return getApi<{ found: boolean; customerId: string | null }>(
    apiClient,
    `/by-guid/${encodeURIComponent(cartGuid)}/owner`,
  );
};

/**
 * The cart the API refused because it belongs to another uid (403
 * `CART_NOT_OWNED`) — e.g. a guest cart adopted after signing in to a
 * different account. The caller re-homes the items onto a fresh cart.
 */
export class CartNotOwnedError extends Error {
  readonly code = "CART_NOT_OWNED" as const;
  constructor() {
    super("This cart belongs to another customer.");
    this.name = "CartNotOwnedError";
  }
}

/** `mustExist` write on a cart that is gone (404 `CART_NOT_FOUND`). */
export class CartNotFoundError extends Error {
  readonly code = "CART_NOT_FOUND" as const;
  constructor() {
    super("Cart not found or expired.");
    this.name = "CartNotFoundError";
  }
}

/**
 * Every cart call is bounded. The API client's own ceiling is 2 minutes; a
 * guest waiting on a cart write must hear back well before that (checkout
 * start additionally races its own 15 s bound).
 */
export const CART_API_TIMEOUT_MS = 15_000;

const translateCartError = (error: unknown): never => {
  const status = statusOf(error);
  const body = errorBodyOf(error);
  const code = typeof body?.code === "string" ? body.code : undefined;
  if (status === 403 && code === "CART_NOT_OWNED") throw new CartNotOwnedError();
  if (status === 410) {
    throw new CartAlreadyConvertedError(
      typeof body?.orderGuid === "string" ? body.orderGuid : undefined,
    );
  }
  if (status === 404 && code === "CART_NOT_FOUND") throw new CartNotFoundError();
  // Anything else — a bare 404 included — is a failure, surfaced as-is. The
  // cart routes have been live in production since 2026-10-10 6:58 PM EDT, so
  // a 404 with no cart code is no longer "this API predates the routes"; the
  // client-SDK Firestore fallback it used to select is gone (App Check
  // enforced on Firestore refuses exactly the guests the API path exists for).
  throw error;
};

export interface UpsertCartResult {
  /** The cart the server persisted — the server's cartId; a fresh guid when `rehomed`. */
  cart: Cart;
  rehomed: boolean;
  convertedOrderGuid?: string;
}

/**
 * Persist the caller's cart through the API (`PUT /carts/:guid`) — the
 * storefront and checkout cart write. Replaces the client-SDK Firestore upsert
 * that App Check refused for some real iPhone Safari guests. The server keys
 * the cart by guid, allocates its numeric id, and re-homes a cart that already
 * became an order (`rehomed: true` + a fresh guid).
 *
 * `mustExist` (checkout edits): never create — CartNotFoundError when the cart
 * is gone, CartAlreadyConvertedError when it became an order.
 */
const upsertCart = async (
  cart: Cart,
  options: { mustExist?: boolean } = {},
): Promise<UpsertCartResult> => {
  let body: ApiEnvelope<{
    cart: Record<string, unknown>;
    rehomed: boolean;
    convertedOrderGuid?: string;
  }>;
  try {
    body = await putApi(
      apiClient,
      `/${encodeURIComponent(cart.guid)}`,
      { cart: cart.toJSON(), ...(options.mustExist ? { mustExist: true } : {}) },
      { timeout: CART_API_TIMEOUT_MS },
    );
  } catch (error) {
    return translateCartError(error);
  }
  return {
    cart: Cart.fromJSON(body.data.cart),
    rehomed: body.data.rehomed,
    ...(body.data.convertedOrderGuid
      ? { convertedOrderGuid: body.data.convertedOrderGuid }
      : {}),
  };
};

/**
 * The caller's live cart at this location (`GET /carts/mine`), or null. Throws
 * on a real failure so the caller can fall back to localStorage.
 */
const getMyCart = async (
  tenantId: number,
  locationNumericId: number,
): Promise<Cart | null> => {
  let body: ApiEnvelope<{ cart: Record<string, unknown> | null }>;
  try {
    body = await getApi(
      apiClient,
      `/mine?tenantId=${tenantId}&restaurantId=${locationNumericId}`,
      { timeout: CART_API_TIMEOUT_MS },
    );
  } catch (error) {
    return translateCartError(error);
  }
  const data = body.data.cart;
  if (!data || typeof data.cartId !== "number") return null;
  return Cart.fromJSON(data);
};

/** Delete the caller's cart (`DELETE /carts/:guid`). A placed cart is kept. */
const deleteCart = async (
  cartGuid: string,
  tenantId: number,
  locationNumericId: number,
): Promise<number> => {
  try {
    const body = await deleteApi<ApiEnvelope<{ deleted: number }>>(
      apiClient,
      `/${encodeURIComponent(cartGuid)}?tenantId=${tenantId}&restaurantId=${locationNumericId}`,
      { timeout: CART_API_TIMEOUT_MS },
    );
    return body.data.deleted;
  } catch (error) {
    return translateCartError(error);
  }
};

const handoffClient = createApiClient("/api/v1/auth/checkout-handoff");
handoffClient.defaults.withCredentials = false;

/**
 * Issue the one-time code that signs checkout in as the CURRENT guest for
 * this cart (`POST /auth/checkout-handoff`). The menu page puts it in the
 * checkout link's fragment in place of the guest's ID token.
 */
const issueCheckoutHandoff = async (cartGuid: string): Promise<string> => {
  const body = await postApi<ApiEnvelope<{ code: string; expiresAt: string }>>(
    handoffClient,
    "",
    { cartGuid },
  );
  return body.data.code;
};

export const cartsApi = {
  getCartByGuid,
  upsertCart,
  getMyCart,
  deleteCart,
  getCartOwnerByGuid,
  issueCheckoutHandoff,
};
